import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { TIME_ZONES, TITLES, US_STATES } from '../../../utils/constants';

/**
 * Standard row layout for AddFormModel person forms.
 * Tab order follows DOM: each row left → right, then next row.
 */
export function buildPersonFormRows({
  formData,
  errors,
  isEditMode,
  handleInputChange,
  showPassword,
  showConfirmPassword,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  phoneFieldName = 'homePhone',
  phoneLabel = 'Home Phone',
  phoneRequired = true,
  lastRowRight = null,
}) {
  const phoneValue = formData[phoneFieldName] ?? '';

  return [
    {
      key: 'name',
      left: (
        <div className="row-name-double">
          <div className="field">
            <label htmlFor="title">Title</label>
            <select id="title" name="title" value={formData.title} onChange={handleInputChange}>
              <option value="">- Select -</option>
              {TITLES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            {errors.title && <span className="error-text">{errors.title}</span>}
          </div>
          <div className="field">
            <label htmlFor="firstName">First Name <span className="required">*</span></label>
            <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} required />
            {errors.firstName && <span className="error-text">{errors.firstName}</span>}
          </div>
        </div>
      ),
      right: (
        <div className="row-middle-last">
          <div className="field">
            <label htmlFor="middleInitial">Middle Initial</label>
            <input
              type="text"
              id="middleInitial"
              name="middleInitial"
              value={formData.middleInitial}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="lastName">Last Name <span className="required">*</span></label>
            <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} required />
            {errors.lastName && <span className="error-text">{errors.lastName}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'address-username',
      left: (
        <div className="field full-width">
          <label htmlFor="addressLine1">Address Line 1 <span className="required">*</span></label>
          <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} required />
          {errors.addressLine1 && <span className="error-text">{errors.addressLine1}</span>}
        </div>
      ),
      right: (
        <div className="field full-width">
          <label htmlFor="username">Username <span className="required">*</span></label>
          <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} required className="bg-blue" />
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>
      ),
    },
    {
      key: 'address2-password',
      left: (
        <div className="field full-width">
          <label htmlFor="addressLine2">Address Line 2</label>
          <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} />
        </div>
      ),
      right: (
        <div className="row-double">
          <div className="field">
            <label htmlFor="password">
              Password {!isEditMode && <span className="required">*</span>}
              <OverlayTrigger placement="top" overlay={<Tooltip>8-64 characters, 1 uppercase, 1 lowercase, 1 number, 1 special char.</Tooltip>}>
                <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
              </OverlayTrigger>
            </label>
            <div className="password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                autoComplete="new-password"
              />
              <span className="eye-icon" onClick={togglePasswordVisibility}>
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </span>
            </div>
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
          <div className="field">
            <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="required">*</span>}</label>
            <div className="password-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                autoComplete="new-password"
              />
              <span className="eye-icon" onClick={toggleConfirmPasswordVisibility}>
                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
              </span>
            </div>
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'city-status',
      left: (
        <div className="row-city-triple">
          <div className="field">
            <label htmlFor="city">City <span className="required">*</span></label>
            <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} required />
            {errors.city && <span className="error-text">{errors.city}</span>}
          </div>
          <div className="row-state-zip">
            <div className="field">
              <label htmlFor="state">State <span className="required">*</span></label>
              <select id="state" name="state" value={formData.state} onChange={handleInputChange} required>
                <option value="">- Select -</option>
                {US_STATES.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
              {errors.state && <span className="error-text">{errors.state}</span>}
            </div>
            <div className="field">
              <label htmlFor="zipCode">Zip Code <span className="required">*</span></label>
              <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleInputChange} required />
              {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
            </div>
          </div>
        </div>
      ),
      right: (
        <div className="field full-width">
          <label htmlFor="status">Status <span className="required">*</span></label>
          <select id="status" name="status" value={formData.status || 'Active'} onChange={handleInputChange}>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          {errors.status && <span className="error-text">{errors.status}</span>}
        </div>
      ),
    },
    {
      key: 'contact-auth',
      left: (
        <div className="row-double">
          <div className="field">
            <label htmlFor="email">Email <span className="required">*</span></label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
              required
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
          <div className="field">
            <label htmlFor="fax">Fax</label>
            <input type="tel" id="fax" name="fax" value={formData.fax} onChange={handleInputChange} />
          </div>
        </div>
      ),
      right: (
        <div className="row-double">
          <div className="field">
            <label htmlFor="timeZone">Time Zone <span className="required">*</span></label>
            <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange}>
              <option value="">- Select -</option>
              {TIME_ZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            {errors.timeZone && <span className="error-text">{errors.timeZone}</span>}
          </div>
          <div className="field">
            <label htmlFor="twoWayAuth">Two Way Authentication <span className="required">*</span></label>
            <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth} onChange={handleInputChange}>
              <option value="">- Select -</option>
              <option value="Enabled">Enabled</option>
              <option value="Disabled">Disabled</option>
            </select>
            {errors.twoWayAuth && <span className="error-text">{errors.twoWayAuth}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'phones',
      left: (
        <div className="row-double">
          <div className="field">
            <label htmlFor={phoneFieldName}>
              {phoneLabel} {phoneRequired && <span className="required">*</span>}
              <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
              </OverlayTrigger>
            </label>
            <input
              type="tel"
              id={phoneFieldName}
              name={phoneFieldName}
              value={phoneValue}
              onChange={handleInputChange}
              required={phoneRequired}
            />
            {errors[phoneFieldName] && <span className="error-text">{errors[phoneFieldName]}</span>}
          </div>
          <div className="field">
            <label htmlFor="cellPhone">
              Cell Phone
              <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX - XXXX</Tooltip>}>
                <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
              </OverlayTrigger>
            </label>
            <input type="tel" id="cellPhone" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} />
            {errors.cellPhone && <span className="error-text">{errors.cellPhone}</span>}
          </div>
        </div>
      ),
      right: lastRowRight,
    },
  ];
}
